import { useEffect, useMemo } from 'react'

import { useDeviceCSVProcessor } from '../hooks/useDeviceCSVProcessor'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { UseFormMethods } from 'react-hook-form'

import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react'

import {
  FormFileUploadControl,
  FormInputSelectControl,
  StyledFormInputControl,
  Table,
  TableBody,
  TableHeader,
} from '@/components/ui'

import { FormInputsIProps } from '../types/types'
import { CSVParseResultI } from '../utils/csvValidator'
import { createColumns } from '../utils/tableColumns'
import { deviceTypes, isValidFileType } from '../utils/utils'

interface BulkUploadFormIProps {
  form: UseFormMethods<FormInputsIProps>
  parseResult?: CSVParseResultI | null
  onParseResultChange?: (result: CSVParseResultI | null) => void
  onIsParsingChange?: (isParsing: boolean) => void
  isRemoveDisabled?: boolean
}
export const BulkUploadForm = ({
  form,
  parseResult,
  onParseResultChange,
  onIsParsingChange,
  isRemoveDisabled = false,
}: BulkUploadFormIProps) => {
  const isUpdatedBulkUpload = useFeatureFlag(
    FeatureFlag.updatedDeviceBulkUpload
  )
  const { register, control, errors, watch, trigger } = form
  const fileUpload = watch('file')
  const { isLoading } = useDeviceCSVProcessor(
    isUpdatedBulkUpload ? fileUpload : null,
    onParseResultChange ?? (() => {})
  )

  useEffect(() => {
    onIsParsingChange?.(!!isLoading)
  }, [isLoading, onIsParsingChange])

  const columns = useMemo(() => createColumns(), [])
  const table = useReactTable({
    data: parseResult?.data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  if (!isUpdatedBulkUpload) {
    return (
      <Box>
        <Box mb='3'>
          <FormInputSelectControl
            control={control}
            errorMessage={!!errors?.type && 'Select a device type'}
            id='type'
            isClearable
            isInvalid={!!errors?.type}
            label='Device Type'
            options={deviceTypes}
            placeholder='Device Type Selection'
            rules={{ required: true }}
          />
        </Box>

        <StyledFormInputControl mb={3}>
          <Tooltip label='Supported file types include CSV' shouldWrapChildren>
            <FormFileUploadControl
              accept='.csv'
              dataTestId='createFloor_modal_floorplan'
              errorMessage={
                fileUpload &&
                !isValidFileType(fileUpload) &&
                'File should be in .csv format'
              }
              id='file'
              inputRef={register({ required: true })}
              isInvalid={!isValidFileType(fileUpload)}
              label='Devices'
              onFileRemoved={() => trigger(['file'])}
              placeholder='Attach CSV'
            />
          </Tooltip>
        </StyledFormInputControl>
      </Box>
    )
  }

  return (
    <Box px='6'>
      <StyledFormInputControl mb={3}>
        <Tooltip
          isDisabled={isValidFileType(fileUpload)}
          label='Supported file types include CSV'
          shouldWrapChildren
        >
          <FormFileUploadControl
            accept='.csv'
            dataTestId='createDevice_modal_bulkUpload'
            errorMessage={
              fileUpload &&
              !isValidFileType(fileUpload) &&
              'File should be in .csv format'
            }
            id='file'
            inputRef={register({ required: true })}
            isDisabled={isRemoveDisabled}
            isInvalid={!isValidFileType(fileUpload)}
            label='Devices'
            onFileRemoved={() => {
              trigger(['file'])
              onParseResultChange?.(null)
            }}
            placeholder='Attach CSV'
          />
        </Tooltip>
      </StyledFormInputControl>

      {isLoading && (
        <Box mb={3}>
          <Text color='gray.600' fontSize='sm'>
            Parsing CSV file...
          </Text>
        </Box>
      )}

      {parseResult && parseResult.errors.length > 0 && (
        <Alert mb={3} status='error'>
          <AlertIcon />
          <VStack align='start' spacing={1}>
            <AlertTitle>CSV Parsing Errors</AlertTitle>
            <AlertDescription>
              {parseResult.errors.map((error, index) => (
                <Text fontSize='sm' key={index}>
                  • {error}
                </Text>
              ))}
            </AlertDescription>
          </VStack>
        </Alert>
      )}

      {parseResult && parseResult.data.length > 0 && (
        <Box mb={3}>
          <Table>
            <TableHeader table={table} />
            <TableBody rowModel={table.getRowModel()} />
          </Table>
        </Box>
      )}
    </Box>
  )
}
